// Resume45.js - Fixed pagination and section ordering
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  Divider,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  DragIndicator,
  Reorder,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationOnIcon,
  LinkedIn as LinkedInIcon,
  Language as LanguageIcon,
  DriveEta as DriveEtaIcon,
} from '@mui/icons-material';

// Default section order
const defaultSectionOrder = [
  'summary',
  'experience',
  'education',
  'certifications',
  'skills',
  'languages',
  'accomplishments',
  'additionalInfo',
  'softwareSkills',
  'volunteering',
  'interests',
  'websites',
  'references'
];

const pageSizeMap = {
  A4: { width: '210mm', minHeight: '297mm', height: '297mm' },
  Letter: { width: '216mm', minHeight: '279mm', height: '279mm' },
  Legal: { width: '216mm', minHeight: '356mm', height: '356mm' },
  A3: { width: '297mm', minHeight: '420mm', height: '420mm' },
  Executive: { width: '184mm', minHeight: '267mm', height: '267mm' },
};

const pageSizePxMap = {
  A4:        { width: 794,  height: 1123 },
  Letter:    { width: 816,  height: 1056 },
  Legal:     { width: 816,  height: 1344 },
  A3:        { width: 1123, height: 1587 },
  Executive: { width: 696,  height: 1010 },
};

const Resume45 = ({
  theme = "#1e88e5",
  color = "#1e88e5",
  font = "Georgia",
  fontSize = "14px",
  fontStyle = "normal",
  headingSize = "18px",
  sectionSpacing = 20,
  paragraphSpacing = 8,
  lineSpacing = 18,
  topBottomMargin = 25,
  sideMargins = 25,
  pageSize = "A4",
  formData = {},
  exporting = false,
  enableDragDrop = true,
}) => {
  const [themeColor, setThemeColor] = useState(color || theme);
  const [currentPage, setCurrentPage] = useState(0);
  const resumeRef = useRef(null);
  const [totalPages, setTotalPages] = useState(1);
  const [pageContents, setPageContents] = useState({});
  const contentRef = useRef(null);

  const page = pageSizeMap[pageSize] || pageSizeMap.A4;
  const pagePx = pageSizePxMap[pageSize] || pageSizePxMap.A4;

  // Spacing helpers
  const itemSpacing = `${paragraphSpacing}px`;
  const sectionDividerSpacing = `${sectionSpacing}px`;
  
  // State for all data sections
  const [personalData, setPersonalData] = useState({});
  const [savedSummary, setSavedSummary] = useState('');
  const [savedSkills, setSavedSkills] = useState([]);
  const [educationEntries, setEducationEntries] = useState([]);
  const [workExperiences, setWorkExperiences] = useState([]);
  const [savedCertifications, setSavedCertifications] = useState([]);
  const [savedLanguages, setSavedLanguages] = useState([]);
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [savedAdditionalInfo, setSavedAdditionalInfo] = useState([]);
  const [savedSoftware, setSavedSoftware] = useState([]);
  const [savedReferences, setSavedReferences] = useState([]);
  const [savedVolunteering, setSavedVolunteering] = useState([]);
  const [savedInterests, setSavedInterests] = useState([]);
  const [savedWebsites, setSavedWebsites] = useState([]);
  const [customSections, setCustomSections] = useState({});
  
  // Backend data fetching states
  const [loading, setLoading] = useState(true);
  const [backendEducation, setBackendEducation] = useState([]);
  const [backendWork, setBackendWork] = useState([]);
  const [backendReferences, setBackendReferences] = useState([]);
  const [backendVolunteering, setBackendVolunteering] = useState([]);

  // Fallback to backend fetched data if local storage arrays are empty
  const finalWorkExperiences = workExperiences.length > 0 ? workExperiences : backendWork;
  const finalEducationEntries = educationEntries.length > 0 ? educationEntries : backendEducation;
  const finalReferences = [...savedReferences, ...backendReferences].filter((ref, idx, self) => {
    const id = typeof ref === 'object' ? JSON.stringify(ref) : ref;
    return self.findIndex(r => (typeof r === 'object' ? JSON.stringify(r) : r) === id) === idx;
  });
  const finalVolunteering = [...savedVolunteering, ...backendVolunteering].filter((vol, idx, self) => {
    const id = typeof vol === 'object' ? JSON.stringify(vol) : vol;
    return self.findIndex(v => (typeof v === 'object' ? JSON.stringify(v) : v) === id) === idx;
  });
  
  // Section ordering states
  const [sectionOrder, setSectionOrder] = useState(defaultSectionOrder);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [arrangeDialogOpen, setArrangeDialogOpen] = useState(false);

  // Global font style
  const globalFontStyle = {
    fontFamily: font,
    fontSize: fontSize,
    fontStyle: fontStyle,
    lineHeight: `${lineSpacing}px`,
  };

  const getTextStyle = (additionalStyles = {}) => ({
    ...globalFontStyle,
    ...additionalStyles
  });

  // Load all data from localStorage
  useEffect(() => {
    setThemeColor(color || theme);
  }, [color, theme]);

  useEffect(() => {
    if (formData && Object.keys(formData).length > 0) {
      setPersonalData(prev => ({
        ...prev,
        ...formData
      }));
    }
  }, [formData]);

  // Load all data on mount
  useEffect(() => {
    // Load personal data
    const storedPersonalData = localStorage.getItem("personalData");
    let personal = {};
   
    if (storedPersonalData) {
      try {
        personal = JSON.parse(storedPersonalData);
      } catch (e) {
        console.error("Error parsing personalData:", e);
      }
    }
   
    if (formData && Object.keys(formData).length > 0) {
      personal = { ...personal, ...formData };
    }
   
    setPersonalData(personal);
    
    // Load summary
    const storedSummaries = localStorage.getItem("summaries");
    if (storedSummaries) {
      try {
        const summaries = JSON.parse(storedSummaries);
        if (Array.isArray(summaries) && summaries.length > 0) {
          setSavedSummary(summaries[summaries.length - 1]);
        }
      } catch (e) {
        console.error("Error parsing summaries:", e);
      }
    }
    
    // Load education
    const storedEntries = localStorage.getItem("educationEntries");
    if (storedEntries) {
      try {
        setEducationEntries(JSON.parse(storedEntries));
      } catch (e) {
        console.error("Error parsing educationEntries:", e);
      }
    }
    
    // Load skills
    const storedSkills = localStorage.getItem("skills");
    if (storedSkills) {
      try {
        setSavedSkills(JSON.parse(storedSkills));
      } catch (e) {
        console.error("Error parsing skills:", e);
      }
    }
    
    // Load work experiences
    const storedWorkExperiences = localStorage.getItem("workExperiences");
    if (storedWorkExperiences) {
      try {
        setWorkExperiences(JSON.parse(storedWorkExperiences) || []);
      } catch (e) {
        console.error("Error parsing workExperiences:", e);
      }
    }
    
    // Load websites
    const websites = localStorage.getItem("websites");
    if (websites) {
      try {
        setSavedWebsites(JSON.parse(websites) || []);
      } catch (e) {
        console.error("Error parsing websites:", e);
      }
    }
    
    // Load certifications
    const certifications = localStorage.getItem("certificationsList");
    if (certifications) {
      try {
        setSavedCertifications(JSON.parse(certifications) || []);
      } catch (e) {
        console.error("Error parsing certifications:", e);
      }
    }
    
    // Load languages
    const languages = localStorage.getItem("languagesList");
    if (languages) {
      try {
        setSavedLanguages(JSON.parse(languages) || []);
      } catch (e) {
        console.error("Error parsing languages:", e);
      }
    }
    
    // Load accomplishments
    const accomplishments = localStorage.getItem("accomplishmentsList");
    if (accomplishments) {
      try {
        setSavedAccomplishments(JSON.parse(accomplishments) || []);
      } catch (e) {
        console.error("Error parsing accomplishments:", e);
      }
    }
    
    // Load additional info
    const additionalInfo = localStorage.getItem("additionalInfo");
    if (additionalInfo) {
      try {
        setSavedAdditionalInfo(JSON.parse(additionalInfo) || []);
      } catch (e) {
        console.error("Error parsing additionalInfo:", e);
      }
    }
    
    // Load software skills
    const software = localStorage.getItem("softwareSkills");
    if (software) {
      try {
        setSavedSoftware(JSON.parse(software) || []);
      } catch (e) {
        console.error("Error parsing softwareSkills:", e);
      }
    }
    
    // Load references
    const references = localStorage.getItem("referencesList");
    if (references) {
      try {
        setSavedReferences(JSON.parse(references) || []);
      } catch (e) {
        console.error("Error parsing references:", e);
      }
    }
    
    // Load volunteering
    const volunteering = localStorage.getItem("volunteering");
    if (volunteering) {
      try {
        setSavedVolunteering(JSON.parse(volunteering) || []);
      } catch (e) {
        console.error("Error parsing volunteering:", e);
      }
    }
    
    // Load interests
    const interests = localStorage.getItem("interests");
    if (interests) {
      try {
        setSavedInterests(JSON.parse(interests) || []);
      } catch (e) {
        console.error("Error parsing interests:", e);
      }
    }
    
    // Load custom sections
    const userId = localStorage.getItem("userId");
    let sectionsData = {};
    if (userId) {
      const currentCustomSections = localStorage.getItem(`current_custom_sections_${userId}`);
     
      if (currentCustomSections) {
        try {
          const customSectionsList = JSON.parse(currentCustomSections);
         
          if (Array.isArray(customSectionsList) && customSectionsList.length > 0) {
            customSectionsList.forEach(sectionName => {
              const userCustomKey = `custom_${userId}_${sectionName}`;
              try {
                const customSectionData = localStorage.getItem(userCustomKey);
                if (customSectionData) {
                  const parsedData = JSON.parse(customSectionData);
                  const items = parsedData.items || [];
                  const validItems = items.filter(item => item && item.trim() !== '');
                 
                  if (validItems.length > 0) {
                    sectionsData[sectionName] = validItems;
                  }
                }
              } catch (error) {
                console.error(`Error loading custom section ${sectionName}:`, error);
              }
            });
          }
        } catch (e) {
          console.error("Error parsing custom sections:", e);
        }
      }
    }
    setCustomSections(sectionsData);
    
    // Load saved section order
    const savedSectionOrder = localStorage.getItem("resume45SectionOrder");
    if (savedSectionOrder) {
      try {
        const parsedOrder = JSON.parse(savedSectionOrder);
        const currentCustomSectionIds = Object.keys(sectionsData).map(name => `custom_${name}`);
        const filteredOrder = parsedOrder.filter(section => !section.startsWith('custom_'));
        const allSections = [...filteredOrder, ...currentCustomSectionIds];
        setSectionOrder(allSections);
      } catch (e) {
        console.error("Error parsing section order:", e);
        const currentCustomSectionIds = Object.keys(sectionsData).map(name => `custom_${name}`);
        setSectionOrder([...defaultSectionOrder, ...currentCustomSectionIds]);
      }
    } else {
      const currentCustomSectionIds = Object.keys(sectionsData).map(name => `custom_${name}`);
      const updatedSectionOrder = [...defaultSectionOrder, ...currentCustomSectionIds];
      setSectionOrder(updatedSectionOrder);
    }

    // Fetch backend data
    const fetchAllData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setLoading(false);
          return;
        }

        // Fetch contact details
        try {
          const contactRes = await axios.get("https://resume-maker-lc.onrender.com/api/contact/me", {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (contactRes.data) {
            setPersonalData(prev => ({
              ...prev,
              firstName:       contactRes.data.firstName       || prev.firstName       || "",
              lastName:        contactRes.data.lastName        || prev.lastName        || "",
              city:            contactRes.data.city            || prev.city            || "",
              country:         contactRes.data.country         || prev.country         || "",
              pinCode:         contactRes.data.pinCode         || prev.pinCode         || "",
              phone:           contactRes.data.phone           || prev.phone           || "",
              email:           contactRes.data.email           || prev.email           || "",
              currentPosition: contactRes.data.currentPosition || prev.currentPosition || "",
              linkedIn:        contactRes.data.linkedIn        || prev.linkedIn        || "",
              website:         contactRes.data.website         || prev.website         || "",
              drivingLicence:  contactRes.data.drivingLicence  || prev.drivingLicence  || "",
            }));
          }
        } catch (e) {
          console.log("No contact data found");
        }

        // Fetch work experience
        try {
          const workRes = await axios.get("https://resume-maker-lc.onrender.com/api/work/me", {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (workRes.data && Array.isArray(workRes.data)) {
            const mappedWork = workRes.data.map(work => ({
              jobTitle: work.jobTitle || work.position || work.role || "",
              employer: work.employer || work.company || work.companyName || "",
              startMonth: work.startMonth || "",
              startYear: work.startYear || work.startDate || "",
              current: work.current !== undefined ? work.current : work.currentlyWorking || false,
              endMonth: work.endMonth || "",
              endYear: work.endYear || work.endDate || "",
              description: work.description || ""
            }));
            setBackendWork(mappedWork);
          }
        } catch (e) {
          console.log("No work history found");
        }

        // Fetch education
        try {
          const eduRes = await axios.get("https://resume-maker-lc.onrender.com/api/education/me", {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (eduRes.data && Array.isArray(eduRes.data)) {
            const mappedEdu = eduRes.data.map(edu => ({
              institutionName: edu.schoolName || edu.collegeName || edu.school || edu.institution || edu.universityName || "",
              degreeName: edu.degreeName || edu.degree || edu.qualification || edu.standard || "",
              fieldOfStudy: edu.specialization || edu.stream || edu.fieldOfStudy || "",
              gradMonth: edu.gradMonth || "",
              gradYear: edu.gradYear || edu.endYear || ""
            }));
            setBackendEducation(mappedEdu);
          }
        } catch (e) {
          console.log("No education found");
        }

        // Fetch references
        try {
          const refRes = await axios.get("https://resume-maker-lc.onrender.com/api/references/me", {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (refRes.data && Array.isArray(refRes.data)) {
            setBackendReferences(refRes.data);
          }
        } catch (e) {
          console.log("No references found");
        }

        // Fetch volunteering
        try {
          const volRes = await axios.get("https://resume-maker-lc.onrender.com/api/volunteering/me", {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (volRes.data && Array.isArray(volRes.data)) {
            setBackendVolunteering(volRes.data);
          }
        } catch (e) {
          console.log("No volunteering found");
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  // Update section order when custom sections change
  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (userId && Object.keys(customSections).length > 0) {
      const currentOrder = [...sectionOrder];
      const currentCustomSectionIds = Object.keys(customSections).map(name => `custom_${name}`);
      const filteredOrder = currentOrder.filter(section => !section.startsWith('custom_'));
      const newOrder = [...filteredOrder, ...currentCustomSectionIds];
     
      if (JSON.stringify(newOrder) !== JSON.stringify(currentOrder)) {
        setSectionOrder(newOrder);
        localStorage.setItem("resume45SectionOrder", JSON.stringify(newOrder));
      }
    }
  }, [customSections]);

  // Save section order to localStorage
  useEffect(() => {
    if (sectionOrder.length > 0) {
      localStorage.setItem("resume45SectionOrder", JSON.stringify(sectionOrder));
    }
  }, [sectionOrder]);

  const formatDate = (month, year) => {
    if (!month || !year) return "";
    return `${month} ${year}`;
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ index, sectionId: sectionOrder[index] }));
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    const data = JSON.parse(e.dataTransfer.getData('text/plain'));
    const sourceIndex = data.index;
    if (sourceIndex !== targetIndex) {
      const newSectionOrder = [...sectionOrder];
      const [movedSection] = newSectionOrder.splice(sourceIndex, 1);
      newSectionOrder.splice(targetIndex, 0, movedSection);
      setSectionOrder(newSectionOrder);
    }
    setDragOverIndex(null);
    e.currentTarget.style.opacity = '1';
  };

  const handleDragEnd = (e) => {
    setDragOverIndex(null);
    e.currentTarget.style.opacity = '1';
  };

  const resetSectionOrder = () => {
    setSectionOrder(defaultSectionOrder);
    localStorage.removeItem("resume45SectionOrder");
  };

  const getSectionTitle = (sectionId) => {
    const titles = {
      'summary': 'PROFILE',
      'skills': 'SKILLS',
      'experience': 'WORK EXPERIENCE',
      'education': 'EDUCATION',
      'certifications': 'CERTIFICATIONS',
      'languages': 'LANGUAGES',
      'accomplishments': 'ACCOMPLISHMENTS',
      'additionalInfo': 'ADDITIONAL INFORMATION',
      'softwareSkills': 'SOFTWARE SKILLS',
      'volunteering': 'VOLUNTEERING',
      'interests': 'INTERESTS',
      'websites': 'WEBSITES & PROFILES',
      'references': 'REFERENCES'
    };
    
    if (sectionId.startsWith('custom_')) {
      const customName = sectionId.replace('custom_', '').replace(/_/g, ' ');
      return customName.toUpperCase();
    }
   
    return titles[sectionId] || sectionId.toUpperCase();
  };

  const renderSectionContent = (sectionId) => {
    switch (sectionId) {
      case 'summary':
        return savedSummary ? (
          <Box className="resume45-section" sx={{ mb: sectionDividerSpacing }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#000',
                mb: itemSpacing,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                ...globalFontStyle,
                fontSize: headingSize
              }}
            >
              PROFILE
            </Typography>
            <Typography sx={{ lineHeight: 1.7, fontSize: '0.95rem', ...globalFontStyle }}>
              {savedSummary}
            </Typography>
          </Box>
        ) : null;
        
      case 'skills':
        return savedSkills.length > 0 ? (
          <Box className="resume45-section" sx={{ mb: sectionDividerSpacing }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#000',
                mb: itemSpacing,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                ...globalFontStyle,
                fontSize: headingSize
              }}
            >
              SKILLS
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {savedSkills.map((skill, index) => (
                <Typography
                  key={index}
                  sx={{
                    ...globalFontStyle,
                    fontSize: '0.9rem',
                    '&:not(:last-child)::after': {
                      content: '"•"',
                      mx: 1,
                      color: '#666'
                    }
                  }}
                >
                  {typeof skill === 'string' ? skill : skill.name}
                </Typography>
              ))}
            </Box>
          </Box>
        ) : null;
        
      case 'experience':
        return finalWorkExperiences.length > 0 ? (
          <Box className="resume45-section" sx={{ mb: sectionDividerSpacing }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#000',
                mb: itemSpacing,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                ...globalFontStyle,
                fontSize: headingSize
              }}
            >
              WORK EXPERIENCE
            </Typography>
            {finalWorkExperiences.map((work, index) => (
              <Box key={index} sx={{ mb: itemSpacing }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '1rem',
                    color: '#000',
                    ...globalFontStyle
                  }}
                >
                  {work.jobTitle?.toUpperCase()}
                </Typography>
                <Typography sx={{ fontStyle: 'italic', color: '#555', fontSize: '0.9rem', mb: 0.5, ...globalFontStyle }}>
                  {work.employer} • {formatDate(work.startMonth, work.startYear)} – {work.current ? "Present" : formatDate(work.endMonth, work.endYear)}
                </Typography>
                {work.description && (
                  <Typography component="div" sx={{ fontSize: '0.9rem', lineHeight: 1.6, ...globalFontStyle }}>
                    {(work.description || '').split('\n').map((line, i) => (
                      line.trim().startsWith('•') ? (
                        <li key={i} style={{ marginLeft: '15px' }}>{line.replace('•', '').trim()}</li>
                      ) : line.trim() ? (
                        <Typography key={i} sx={{ fontSize: '0.9rem', ...globalFontStyle }}>{line}</Typography>
                      ) : null
                    ))}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        ) : null;
        
      case 'education':
        return finalEducationEntries.length > 0 ? (
          <Box className="resume45-section" sx={{ mb: sectionDividerSpacing }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#000',
                mb: itemSpacing,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                ...globalFontStyle,
                fontSize: headingSize
              }}
            >
              EDUCATION
            </Typography>
            {finalEducationEntries.map((edu, index) => {
              const institutionName = edu.institutionName || (edu.educationLevel === "school" ? edu.schoolName : edu.collegeName) || edu.school || edu.institution || edu.universityName;
              const degreeName = edu.degreeName || (edu.educationLevel === "school" ? edu.standard : edu.degree) || edu.qualification || edu.standard;
              const fieldOfStudy = edu.specialization || (edu.educationLevel === "school" ? edu.stream : edu.fieldOfStudy) || edu.stream;
             
              return (
                <Box key={index} sx={{ mb: itemSpacing }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', ...globalFontStyle }}>
                    {institutionName || degreeName}
                  </Typography>
                  <Typography sx={{ fontSize: '0.85rem', color: '#555', fontStyle: 'italic', ...globalFontStyle }}>
                    {formatDate(edu.gradMonth, edu.gradYear)} {degreeName && `• ${degreeName}`} {fieldOfStudy && `in ${fieldOfStudy}`}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        ) : null;
        
      case 'certifications':
        return savedCertifications.length > 0 ? (
          <Box className="resume45-section" sx={{ mb: sectionDividerSpacing }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#000',
                mb: itemSpacing,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                ...globalFontStyle,
                fontSize: headingSize
              }}
            >
              CERTIFICATIONS
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {savedCertifications.map((cert, index) => (
                <Typography
                  key={index}
                  sx={{
                    ...globalFontStyle,
                    fontSize: '0.9rem',
                    '&:not(:last-child)::after': {
                      content: '"•"',
                      mx: 1,
                      color: '#666'
                    }
                  }}
                >
                  {typeof cert === 'string' ? cert : cert.name}
                </Typography>
              ))}
            </Box>
          </Box>
        ) : null;
        
      case 'languages':
        return savedLanguages.length > 0 ? (
          <Box className="resume45-section" sx={{ mb: sectionDividerSpacing }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#000',
                mb: itemSpacing,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                ...globalFontStyle,
                fontSize: headingSize
              }}
            >
              LANGUAGES
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {savedLanguages.map((lang, index) => (
                <Typography
                  key={index}
                  sx={{
                    ...globalFontStyle,
                    fontSize: '0.9rem',
                    '&:not(:last-child)::after': {
                      content: '"•"',
                      mx: 1,
                      color: '#666'
                    }
                  }}
                >
                  {typeof lang === 'string' ? lang : `${lang.name} ${lang.level ? `(${lang.level})` : ''}`}
                </Typography>
              ))}
            </Box>
          </Box>
        ) : null;
        
      case 'accomplishments':
        return savedAccomplishments.length > 0 ? (
          <Box className="resume45-section" sx={{ mb: sectionDividerSpacing }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#000',
                mb: itemSpacing,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                ...globalFontStyle,
                fontSize: headingSize
              }}
            >
              ACCOMPLISHMENTS
            </Typography>
            <Typography sx={{ fontSize: '0.9rem', lineHeight: 1.6, ...globalFontStyle }}>
              {savedAccomplishments.join(' • ')}
            </Typography>
          </Box>
        ) : null;
        
      case 'additionalInfo':
        return savedAdditionalInfo.length > 0 ? (
          <Box className="resume45-section" sx={{ mb: sectionDividerSpacing }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#000',
                mb: itemSpacing,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                ...globalFontStyle,
                fontSize: headingSize
              }}
            >
              ADDITIONAL INFO
            </Typography>
            <Typography sx={{ fontSize: '0.9rem', lineHeight: 1.6, ...globalFontStyle }}>
              {savedAdditionalInfo.join(' • ')}
            </Typography>
          </Box>
        ) : null;
        
      case 'softwareSkills':
        return savedSoftware.length > 0 ? (
          <Box className="resume45-section" sx={{ mb: sectionDividerSpacing }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#000',
                mb: itemSpacing,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                ...globalFontStyle,
                fontSize: headingSize
              }}
            >
              SOFTWARE SKILLS
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {savedSoftware.map((software, index) => (
                <Typography
                  key={index}
                  sx={{
                    ...globalFontStyle,
                    fontSize: '0.9rem',
                    '&:not(:last-child)::after': {
                      content: '"•"',
                      mx: 1,
                      color: '#666'
                    }
                  }}
                >
                  {typeof software === 'string' ? software : software.name}
                </Typography>
              ))}
            </Box>
          </Box>
        ) : null;
        
      case 'volunteering':
        return finalVolunteering.length > 0 ? (
          <Box className="resume45-section" sx={{ mb: sectionDividerSpacing }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#000',
                mb: itemSpacing,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                ...globalFontStyle,
                fontSize: headingSize
              }}
            >
              VOLUNTEERING
            </Typography>
            {finalVolunteering.map((vol, index) => {
              const volData = typeof vol === 'object' ? vol : { subtopic: vol, fromDate: '', toDate: '' };
              return (
                <Box key={index} sx={{ mb: itemSpacing }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', ...globalFontStyle }}>
                    {volData.subtopic || volData.role || volData.title || 'Volunteer'}
                  </Typography>
                  {volData.fromDate && (
                    <Typography sx={{ fontSize: '0.85rem', color: '#555', ...globalFontStyle }}>
                      {volData.fromDate} {volData.toDate && `- ${volData.toDate}`}
                    </Typography>
                  )}
                  {volData.content && (
                    <Typography sx={{ fontSize: '0.85rem', ...globalFontStyle }}>
                      {volData.content}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        ) : null;
        
      case 'interests':
        return savedInterests.length > 0 ? (
          <Box className="resume45-section" sx={{ mb: sectionDividerSpacing }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#000',
                mb: itemSpacing,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                ...globalFontStyle,
                fontSize: headingSize
              }}
            >
              INTERESTS
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {savedInterests.map((interest, index) => (
                <Typography
                  key={index}
                  sx={{
                    ...globalFontStyle,
                    fontSize: '0.9rem',
                    '&:not(:last-child)::after': {
                      content: '"•"',
                      mx: 1,
                      color: '#666'
                    }
                  }}
                >
                  {interest}
                </Typography>
              ))}
            </Box>
          </Box>
        ) : null;
        
      case 'websites':
        return savedWebsites.length > 0 ? (
          <Box className="resume45-section" sx={{ mb: sectionDividerSpacing }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#000',
                mb: itemSpacing,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                ...globalFontStyle,
                fontSize: headingSize
              }}
            >
              WEBSITES
            </Typography>
            <Typography sx={{ fontSize: '0.9rem', ...globalFontStyle }}>
              {savedWebsites.map(site => typeof site === 'string' ? site : site.url || site.name).join(' • ')}
            </Typography>
          </Box>
        ) : null;
        
      case 'references':
        return finalReferences.length > 0 ? (
          <Box className="resume45-section" sx={{ mb: sectionDividerSpacing }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#000',
                mb: itemSpacing,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                ...globalFontStyle,
                fontSize: headingSize
              }}
            >
              REFERENCES
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {finalReferences.map((ref, index) => {
                if (typeof ref === 'string') {
                  return (
                    <Typography key={index} sx={{ fontSize: '0.9rem', lineHeight: 1.6, ...globalFontStyle }}>
                      {ref}
                    </Typography>
                  );
                }
                return (
                  <Box key={index} sx={{ minWidth: '200px' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', ...globalFontStyle }}>
                      {ref.refName || ref.name || ref.referenceName}
                    </Typography>
                    <Typography sx={{ fontSize: '0.85rem', color: '#555', ...globalFontStyle }}>
                      {ref.company || ref.companyName}
                      {(ref.position || ref.jobTitle) && ` • ${ref.position || ref.jobTitle}`}
                    </Typography>
                    {ref.phone && (
                      <Typography sx={{ fontSize: '0.85rem', color: '#555', ...globalFontStyle }}>
                        Phone: {ref.phone}
                      </Typography>
                    )}
                    {ref.email && (
                      <Typography sx={{ fontSize: '0.85rem', color: '#555', ...globalFontStyle }}>
                        Email: {ref.email}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        ) : null;
        
      default:
        if (sectionId && sectionId.startsWith('custom_')) {
          const customSectionName = sectionId.replace('custom_', '');
          const items = customSections[customSectionName] || [];
          const validItems = items.filter(item => item && item.trim() !== '');
         
          if (validItems.length > 0) {
            return (
              <Box className="resume45-section" sx={{ mb: sectionDividerSpacing }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: '#000',
                    mb: itemSpacing,
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    ...globalFontStyle,
                    fontSize: headingSize
                  }}
                >
                  {customSectionName.replace(/_/g, ' ').toUpperCase()}
                </Typography>
                <Typography sx={{ fontSize: '0.9rem', lineHeight: 1.6, ...globalFontStyle }}>
                  {validItems.slice(0, 3).join(' • ')}
                </Typography>
              </Box>
            );
          }
        }
        return null;
    }
  };

  const renderDragPanel = () => {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <DragIndicator sx={{ mr: 1 }} />
          Resume Sections
          <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
            ({sectionOrder.length} sections)
          </Typography>
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Drag and drop to reorder sections.
        </Typography>
        <Box sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
          {sectionOrder.map((sectionId, index) => (
            <Paper
              key={sectionId}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              sx={{
                p: 2,
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                backgroundColor: dragOverIndex === index ? '#f0f0f0' : 'white',
                boxShadow: dragOverIndex === index ? 2 : 1,
                cursor: 'grab',
                '&:active': { cursor: 'grabbing' },
                transition: 'all 0.2s ease',
              }}
            >
              <DragIndicator color="action" />
              <Typography sx={{ ml: 1, flexGrow: 1 }}>
                {getSectionTitle(sectionId)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {index + 1}
              </Typography>
            </Paper>
          ))}
        </Box>
        <Button onClick={resetSectionOrder} variant="outlined" size="small" sx={{ mt: 1 }}>
          Reset Section Order
        </Button>
      </Box>
    );
  };

  const renderHeader = () => (
    <Box className="resume-header-area" sx={{ textAlign: 'center', mb: 3 }}>
      <Typography
        variant="h3"
        component="h1"
        sx={{
          fontWeight: 900,
          letterSpacing: '2px',
          color: '#000',
          fontSize: '2.4rem',
          lineHeight: 1.1,
          mb: 1,
          ...globalFontStyle
        }}
      >
        {(personalData.firstName?.toUpperCase() || 'CONNOR')} {(personalData.lastName?.toUpperCase() || 'HAMILTON')}
      </Typography>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          color: '#333',
          letterSpacing: '1px',
          fontSize: '1.2rem',
          textTransform: 'uppercase',
          mb: 1,
          ...globalFontStyle
        }}
      >
        {personalData.currentPosition || 'Real Estate Agent'}
      </Typography>
      
      {/* Contact Info - First Row */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap', mb: 0.5 }}>
        {personalData.phone && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PhoneIcon sx={{ fontSize: 14, color: '#555' }} />
            <Typography sx={{ fontSize: '0.9rem', color: '#444', ...globalFontStyle }}>
              {personalData.phone}
            </Typography>
          </Box>
        )}
        {personalData.email && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <EmailIcon sx={{ fontSize: 14, color: '#555' }} />
            <Typography sx={{ fontSize: '0.9rem', color: '#444', ...globalFontStyle }}>
              {personalData.email}
            </Typography>
          </Box>
        )}
      </Box>
      
      {/* Location */}
      {personalData.city && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <LocationOnIcon sx={{ fontSize: 14, color: '#555' }} />
          <Typography sx={{ fontSize: '0.85rem', color: '#555', ...globalFontStyle }}>
            {personalData.city}
          </Typography>
        </Box>
      )}
      
      {/* LinkedIn, Website, Driving Licence */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
        {personalData.linkedIn && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LinkedInIcon sx={{ fontSize: 14, color: '#0077b5' }} />
            <a 
              href={personalData.linkedIn.startsWith('http') ? personalData.linkedIn : `https://${personalData.linkedIn}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ 
                color: '#0077b5', 
                textDecoration: 'none',
                fontSize: '0.85rem',
                fontFamily: font
              }}
            >
              {personalData.linkedIn.replace(/^https?:\/\/(www\.)?linkedin\.com\/in\/|linkedin\.com\/in\//, '')}
            </a>
          </Box>
        )}
        
        {personalData.website && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LanguageIcon sx={{ fontSize: 14, color: '#1976d2' }} />
            <a 
              href={personalData.website.startsWith('http') ? personalData.website : `https://${personalData.website}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ 
                color: '#1976d2', 
                textDecoration: 'none',
                fontSize: '0.85rem',
                fontFamily: font
              }}
            >
              {personalData.website.replace(/^https?:\/\/(www\.)?/, '')}
            </a>
          </Box>
        )}
        
        {personalData.drivingLicence && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <DriveEtaIcon sx={{ fontSize: 14, color: '#4caf50' }} />
            <Typography sx={{ fontSize: '0.85rem', color: '#555', ...globalFontStyle }}>
              DL: {personalData.drivingLicence}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  // FIXED: Pagination function that properly distributes content across pages
  const paginateSections = () => {
    if (!contentRef.current) return;

    const sections = Array.from(contentRef.current.querySelectorAll(".resume45-section"));
    const pageHeight = pagePx.height - (topBottomMargin * 2);
    
    const headerEl = contentRef.current.querySelector(".resume-header-area");
    const headerHeight = headerEl ? headerEl.getBoundingClientRect().height : 180;
    const dividerHeight = 20;
    const headerTotal = headerHeight + dividerHeight;

    const pageGroups = [];
    let currentGroup = [];
    let accumulatedHeight = 0;
    let isFirstPage = true;

    sections.forEach((section) => {
      const sectionHeight = section.getBoundingClientRect().height || 50;
      const availableHeight = pageHeight - (isFirstPage ? headerTotal : 0);

      // If adding this section exceeds the page height, start a new page
      if (accumulatedHeight + sectionHeight > availableHeight && currentGroup.length > 0) {
        pageGroups.push([...currentGroup]);
        currentGroup = [];
        accumulatedHeight = 0;
        isFirstPage = false;
      }

      currentGroup.push(section);
      accumulatedHeight += sectionHeight;
    });

    // Push the last group
    if (currentGroup.length > 0) {
      pageGroups.push([...currentGroup]);
    }

    // If no sections, at least one page
    if (pageGroups.length === 0) {
      pageGroups.push([]);
    }

    // Map each group to section IDs
    const allSectionIds = sectionOrder.filter(id => renderSectionContent(id) !== null);
    const newPageContents = {};

    pageGroups.forEach((group, index) => {
      const sectionIds = group.map(section => {
        const sectionIndex = Array.from(contentRef.current.querySelectorAll(".resume45-section")).indexOf(section);
        return allSectionIds[sectionIndex] || null;
      }).filter(id => id !== null);

      newPageContents[index] = sectionIds;
    });

    setTotalPages(Math.max(1, pageGroups.length));
    setPageContents(newPageContents);

    // Clamp current page
    if (currentPage >= pageGroups.length) {
      setCurrentPage(Math.max(0, pageGroups.length - 1));
    }
  };

  // Calculate pagination when dependencies change
  useEffect(() => {
    if (loading) return;

    const timeoutId = setTimeout(() => {
      paginateSections();
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [
    sectionOrder,
    savedSummary,
    finalWorkExperiences,
    finalEducationEntries,
    savedSkills,
    savedLanguages,
    savedAccomplishments,
    savedCertifications,
    finalReferences,
    savedSoftware,
    finalVolunteering,
    savedInterests,
    savedWebsites,
    savedAdditionalInfo,
    customSections,
    loading,
    pageSize,
    topBottomMargin,
  ]);

  // Re-paginate when page size or margins change
  useEffect(() => {
    if (loading) return;
    const timeoutId = setTimeout(() => {
      paginateSections();
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [pageSize, topBottomMargin, sideMargins, sectionSpacing, paragraphSpacing, lineSpacing, fontSize, headingSize]);

  // Get sections for a specific page
  const getSectionsForPage = (pageIndex) => {
    return pageContents[pageIndex] || [];
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
        <Typography variant="h6">Loading resume data...</Typography>
      </Box>
    );
  }

  // Render a single page
  const renderPage = (pageIdx) => {
    const pageSectionIds = getSectionsForPage(pageIdx);

    return (
      <Paper
        key={pageIdx}
        elevation={exporting ? 0 : 3}
        className="resume-page"
        ref={pageIdx === 0 ? resumeRef : null}
        sx={{
          width: page.width,
          height: page.height,
          bgcolor: 'white',
          mx: 'auto',
          mb: exporting ? 0 : '24px',
          p: `${topBottomMargin}px ${sideMargins}px`,
          fontFamily: font,
          color: '#1a1a1a',
          boxSizing: 'border-box',
          overflow: 'hidden',
          ...(exporting && {
            pageBreakAfter: 'always',
            marginBottom: '0',
          }),
        }}
      >
        {pageIdx === 0 && renderHeader()}
        {pageIdx === 0 && <Divider sx={{ borderColor: '#000', borderWidth: 1, my: 2 }} />}
        {pageSectionIds.map((sectionId) => {
          const content = renderSectionContent(sectionId);
          if (!content) return null;
          return (
            <React.Fragment key={sectionId}>
              {content}
            </React.Fragment>
          );
        })}
      </Paper>
    );
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f5f5f5',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: exporting ? 0 : 3,
      }}
    >
      {!exporting && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2, gap: 2 }}>
          <IconButton
            onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
            disabled={currentPage === 0}
            size="small"
            color="primary"
          >
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2">Page {currentPage + 1} of {totalPages}</Typography>
          <IconButton
            onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={currentPage >= totalPages - 1}
            size="small"
            color="primary"
          >
            <ChevronRight />
          </IconButton>
         
          {enableDragDrop && (
            <>
              <Button variant="outlined" startIcon={<Reorder />} onClick={() => setArrangeDialogOpen(true)}>Arrange Sections</Button>
              <Button variant="outlined" color="error" onClick={resetSectionOrder}>Reset All</Button>
            </>
          )}
        </Box>
      )}

      {/* Hidden measurement container for pagination */}
      <Box
        ref={contentRef}
        sx={{
          position: "absolute",
          visibility: "hidden",
          pointerEvents: "none",
          width: page.width,
          p: `${topBottomMargin}px ${sideMargins}px`,
          boxSizing: "border-box",
          top: 0,
          left: -9999,
          zIndex: -1,
        }}
      >
        <Box className="resume-header-area">
          {renderHeader()}
        </Box>
        <Divider sx={{ borderColor: '#000', borderWidth: 1, my: 2 }} />
        {sectionOrder.map((sectionId) => {
          const content = renderSectionContent(sectionId);
          if (!content) return null;
          return (
            <React.Fragment key={sectionId}>
              {content}
            </React.Fragment>
          );
        })}
      </Box>

      {/* Pages Container */}
      <Box id="resume-container">
        {exporting ? (
          // Render all pages sequentially for PDF download
          Array.from({ length: totalPages }).map((_, pageIdx) => renderPage(pageIdx))
        ) : (
          // Render only current page in the editor UI
          renderPage(currentPage)
        )}
      </Box>

      {/* Arrange Sections Dialog */}
      <Dialog open={arrangeDialogOpen} onClose={() => setArrangeDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Reorder sx={{ mr: 1 }} />
            Arrange Resume Sections
          </Box>
        </DialogTitle>
        <DialogContent>
          {renderDragPanel()}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setArrangeDialogOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export const resumeMeta = {
  hasPhoto: false,
  columns: 1,
};

export default Resume45;